/**
 * Verification Utilities
 * 
 * Handles role-specific verification status and requirements
 */

/**
 * Determines if a user is verified for a specific role
 * @param {Object} profile - User profile object
 * @param {string} role - Role to check ('host' or 'traveler')
 * @returns {Object} Verification status object
 */
export const getRoleSpecificVerification = (profile, role) => {
  if (!profile) {
    return {
      isVerified: false,
      verificationLevel: 'none',
      verificationText: 'Unverified Account',
      verificationMethod: null
    };
  }

  const verifications = profile?.verifications || {};
  
  // Check specific verification methods
  const hasEmiratesId = verifications?.emiratesId?.verified || false;
  const hasKyc = verifications?.kyc?.verified || false;
  
  // Role-specific verification logic
  if (role === 'host') {
    // ✅ NEW: For hosts, BOTH KYC and Emirates ID are required
    const hostVerified = hasKyc && hasEmiratesId;
    
    // Determine verification status text
    let verificationText = 'Unverified Account';
    let verificationLevel = 'none';
    
    if (hostVerified) {
      verificationText = 'Verified Account (KYC + Emirates ID)';
      verificationLevel = 'both';
    } else if (hasKyc && !hasEmiratesId) {
      verificationText = 'KYC Verified - Complete Emirates ID Verification';
      verificationLevel = 'kyc';
    } else if (!hasKyc && hasEmiratesId) {
      verificationText = 'Emirates ID Verified - Complete KYC Verification';
      verificationLevel = 'emirates_id';
    } else {
      verificationText = 'Unverified Account - Start with KYC Verification';
      verificationLevel = 'none';
    }
    
    return {
      isVerified: hostVerified,
      verificationLevel: verificationLevel,
      verificationText: verificationText,
      verificationMethod: hostVerified ? 'both' : (hasKyc ? 'kyc' : (hasEmiratesId ? 'emirates_id' : null)),
      hasKyc: hasKyc,
      hasEmiratesId: hasEmiratesId
    };
  } else if (role === 'traveler') {
    // For travelers, only KYC verification counts
    const travelerVerified = hasKyc;
    
    return {
      isVerified: travelerVerified,
      verificationLevel: travelerVerified ? 'kyc' : 'none',
      verificationText: travelerVerified ? 'Verified Account (KYC)' : 'Unverified Account',
      verificationMethod: travelerVerified ? 'kyc' : null
    };
  }
  
  // Default fallback
  return {
    isVerified: false,
    verificationLevel: 'none',
    verificationText: 'Unverified Account',
    verificationMethod: null
  };
};

/**
 * Gets verification requirements for a specific role
 * @param {string} role - Role to get requirements for
 * @returns {string} Requirements text
 */
export const getVerificationRequirements = (role) => {
  if (role === 'host') {
    return 'Verify with Emirates ID to list properties and manage bookings';
  } else if (role === 'traveler') {
    return 'Verify with KYC providers to make bookings and reservations';
  }
  return 'Verification required for full access';
};

/**
 * Checks if user can perform role-specific actions
 * @param {Object} profile - User profile object
 * @param {string} role - Role to check
 * @param {string} action - Action to check (e.g., 'book', 'list')
 * @returns {boolean} Whether user can perform the action
 */
export const canPerformRoleAction = (profile, role, action) => {
  const verification = getRoleSpecificVerification(profile, role);
  
  if (!verification.isVerified) {
    return false;
  }
  
  // Add specific action checks here if needed
  return true;
};

/**
 * Gets verification status for display in UI
 * @param {Object} profile - User profile object
 * @param {string} role - Role to check
 * @returns {Object} UI-ready verification status
 */
export const getVerificationDisplayStatus = (profile, role) => {
  const verification = getRoleSpecificVerification(profile, role);
  
  return {
    ...verification,
    showVerifyButton: !verification.isVerified,
    buttonText: verification.isVerified ? 'Your Profile' : 'Verify now',
    iconType: verification.isVerified ? 'verified' : 'unverified'
  };
};
